import { colors } from '@fikasio/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import uniqBy from 'lodash.uniqby';
import { DatePicker, Icon } from '@fikasio/react-ui-components';
import TrashIcon from 'components/UI/TrashIcon';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import ProjectSelectorButton from 'components/tasks/TasksViewer/ProjectSelectorButton';
import { useAuth } from 'services/login/hooks';
import { sharedAssigneeId } from 'services/users/assignable';
import { setPaneContent } from 'services/pane/slice';
import { projectStatus } from 'components/projects/utils';
import { useGetProjectsQuery } from 'services/projects/api';
import { useAddTaskMutation, useGetTasksQuery, usePatchManyTasksMutation, useDeleteManyTasksMutation } from 'services/tasks/api';
import { TASK_LIST_PAGE_SIZE, Task, TaskListFilter } from 'services/tasks/types';
import { pickedDateTimeInUserZone, toDateTime, toPickerDate } from 'utils/date';
import SelectionBar from 'components/UI/SelectionBar';
import AddTaskButton from './AddTaskButton';
import TasksFilters from './TasksFilters/TasksFilters';
import TasksViewer from './TasksViewer/TasksViewer';
import {
  applyTaskSelection,
  DEFAULT_TASKS_FILTER_DRAFT,
  filterTasks,
  MAX_BATCH_SIZE,
  sortTasks,
  TasksFilterDraft,
  toCreateTaskDefaults,
  toServerTaskFilter,
  toTasksViewFilter,
} from './utils';

interface TasksViewProps {
  defaultFilter?: TasksFilterDraft;
  extraFilter?: TaskListFilter;
  onTaskClick: (taskId: string) => void;
  pageSize?: number;
  paginated?: boolean;
  projectId?: string;
  showAddButton?: boolean;
  showCompletionFilter?: boolean;
  showDueDateFilter?: boolean;
  skip?: boolean;
  tasks?: Task[] | null;
}

const TasksView = ({
  defaultFilter = DEFAULT_TASKS_FILTER_DRAFT,
  extraFilter,
  onTaskClick,
  pageSize = TASK_LIST_PAGE_SIZE,
  paginated = false,
  projectId,
  showAddButton,
  showCompletionFilter,
  showDueDateFilter,
  skip = false,
  tasks: tasksProp,
}: TasksViewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [filterDraft, setFilterDraft] = useState<TasksFilterDraft>(defaultFilter);
  const filter = toTasksViewFilter(filterDraft);
  const [offset, setOffset] = useState(0);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [isDueAtDatepickerOpen, setIsDueAtDatepickerOpen] = useState(false);
  const lastSelectedTaskIdRef = useRef<string | undefined>(undefined);

  const { data: projects } = useGetProjectsQuery();
  const [createTask] = useAddTaskMutation();
  const [patchManyTasks] = usePatchManyTasksMutation();
  const [deleteManyTasks] = useDeleteManyTasksMutation();

  const serverFilter = toServerTaskFilter(filter, extraFilter);
  const taskDefaults = toCreateTaskDefaults(serverFilter, {
    currentUserId: user?.id,
    projectId,
  });
  const extraFilterKey = JSON.stringify(extraFilter ?? {});
  const { data: taskPage } = useGetTasksQuery(
    {
      filter: serverFilter,
      limit: pageSize,
      offset,
      sort: { dueAt: 1, description: 1 },
    },
    { skip: !paginated || skip },
  );

  useEffect(() => {
    setOffset(0);
    lastSelectedTaskIdRef.current = undefined;
    setSelectedTasks([]);
  }, [extraFilterKey]);

  const addTask = async task => {
    return createTask(task);
  };

  const resetPagingAndSelection = () => {
    setOffset(0);
    lastSelectedTaskIdRef.current = undefined;
    setSelectedTasks([]);
  };

  const handleApplyFilters = (next: TasksFilterDraft) => {
    resetPagingAndSelection();
    setFilterDraft(next);
  };

  const displayedTasks = paginated
    ? (taskPage?.tasks || [])
    : sortTasks(filterTasks(tasksProp, filter) || []);
  const total = paginated ? (taskPage?.total || 0) : displayedTasks.length;
  const canGoToPreviousPage = offset > 0;
  const canGoToNextPage = offset + pageSize < total;

  const handleSelectTask = (task: Task, event?: { shiftKey?: boolean }) => {
    const result = applyTaskSelection({
      displayedTasks,
      lastSelectedTaskId: lastSelectedTaskIdRef.current,
      selectedTasks,
      shiftKey: !!event?.shiftKey,
      task,
    });
    lastSelectedTaskIdRef.current = result.lastSelectedTaskId;
    setSelectedTasks(result.selectedTasks);
  };

  const clearSelectedTasks = () => {
    lastSelectedTaskIdRef.current = undefined;
    setSelectedTasks([]);
  };

  const uniqueDueAtForSelectedTasks = uniqBy(
    selectedTasks,
    task => task.dueAt ? toDateTime(task.dueAt)!.toFormat('yyyy-MM-dd') : null,
  ).map(task => task.dueAt);
  const allSelectedTasksHaveSameDueAt = uniqueDueAtForSelectedTasks.length === 1;
  const selectedTaskIds = selectedTasks
    .filter(selectedTask => !!selectedTask.id)
    .map(selectedTask => selectedTask.id!);
  const isOverBatchLimit = selectedTaskIds.length > MAX_BATCH_SIZE;

  const deleteSelectedTasks = () => {
    if (isOverBatchLimit) {
      return;
    }
    deleteManyTasks({ ids: selectedTaskIds })
      .then(result => {
        if (!('error' in result)) {
          clearSelectedTasks();
        }
      });
  };

  const patchSelectedTasks = (
    infos: { assignee?: string | null; dueAt?: Date | null; projects?: { id: string }[] },
    { clearSelection = true } = {},
  ) => {
    if (isOverBatchLimit) {
      return;
    }
    patchManyTasks({ ids: selectedTaskIds, infos })
      .then(result => {
        if ('error' in result) {
          return;
        }
        if (clearSelection) {
          clearSelectedTasks();
          return;
        }
        if (Object.prototype.hasOwnProperty.call(infos, 'assignee')) {
          const assignee = infos.assignee;
          setSelectedTasks(current => current.map(task => (
            task.id && selectedTaskIds.includes(task.id)
              ? { ...task, assignee }
              : task
          )));
        }
      });
  };

  const availableProjects = (projects || [])
    .filter(project => !project.isArchived && projectStatus(project) !== 'Cancelled')
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const assignSelectedTasksToProject = (projectId: string) => {
    patchSelectedTasks({ projects: [{ id: projectId }] }, { clearSelection: false });
  };

  const assignSelectedTasksToUser = (assigneeId: string | null) => {
    patchSelectedTasks({ assignee: assigneeId }, { clearSelection: false });
  };

  return (
    <>
      <TasksViewer
        actions={(showAddButton || showCompletionFilter || showDueDateFilter) && (
          <>
            <TasksFilters
              defaults={defaultFilter}
              onApply={handleApplyFilters}
              showCompletionFilter={showCompletionFilter}
              showDueDateFilter={showDueDateFilter}
              value={filterDraft}
            />
            {
              showAddButton && (
                <AddTaskButton
                  onClick={() => dispatch(
                    setPaneContent({
                      type: 'TASK',
                      id: 'NEW',
                      ...(taskDefaults.assignee ? { assignee: taskDefaults.assignee } : {}),
                      ...(taskDefaults.dueAt ? {
                        dueAt: taskDefaults.dueAt instanceof Date
                          ? taskDefaults.dueAt.toISOString()
                          : taskDefaults.dueAt,
                      } : {}),
                      ...(taskDefaults.projects?.[0]?.id
                        ? { projectId: taskDefaults.projects[0].id }
                        : {}),
                    }),
                  )}
                  showCreateSectionButton={!!projectId}
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    lineHeight: '24px',
                  }}
                />
              )
            }
          </>
        )}
        onAddTask={task => addTask(task)}
        onSelectAllTasks={tasksArray => {
          lastSelectedTaskIdRef.current = tasksArray[tasksArray.length - 1]?.id;
          setSelectedTasks(tasksArray);
        }}
        onTaskClick={onTaskClick}
        onTaskSelect={handleSelectTask}
        projectId={projectId}
        selectedTasks={selectedTasks}
        taskDefaults={taskDefaults}
        tasks={displayedTasks}
      />
      {
        paginated && total > pageSize && (
          <div
            style={{
              alignItems: 'center',
              clear: 'both',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              padding: '8px 0',
            }}
          >
            <span>
              {t('showingXToYOfZ', {
                from: offset + 1,
                to: Math.min(offset + displayedTasks.length, total),
                total,
              })}
            </span>
            <Icon
              name="caret-left"
              onClick={() => {
                if (canGoToPreviousPage) {
                  setOffset(Math.max(0, offset - pageSize));
                }
              }}
              style={{
                cursor: canGoToPreviousPage ? 'pointer' : 'default',
                opacity: canGoToPreviousPage ? 1 : 0.4,
              }}
            />
            <Icon
              name="caret-right"
              onClick={() => {
                if (canGoToNextPage) {
                  setOffset(offset + pageSize);
                }
              }}
              style={{
                cursor: canGoToNextPage ? 'pointer' : 'default',
                opacity: canGoToNextPage ? 1 : 0.4,
              }}
            />
          </div>
        )
      }
      {
        selectedTasks.length > 0 && (
          <SelectionBar data-testid="tasks-selection-bar">
            <Icon
              name="times"
              onClick={clearSelectedTasks}
              style={{
                cursor: 'pointer',
                marginRight: 10,
                width: 25,
              }}
            />
            <b>
              {t('xSelectedTasks', { count: selectedTasks.length })}
            </b>
            <TrashIcon
              onClick={deleteSelectedTasks}
              size="1x"
              style={{
                color: colors.danger,
                cursor: 'pointer',
                marginLeft: 10,
              }}
            />
            <div
              onClick={() => {
                if (!isOverBatchLimit) {
                  setIsDueAtDatepickerOpen(true);
                }
              }}
              style={{
                cursor: 'pointer',
                width: 150,
              }}
            >
              <DatePicker
                defaultValue={
                  (allSelectedTasksHaveSameDueAt && uniqueDueAtForSelectedTasks[0])
                  ? toPickerDate(uniqueDueAtForSelectedTasks[0])
                  : undefined
                }
                isOpen={isDueAtDatepickerOpen}
                onChange={dueAt => {
                  const timestamp = pickedDateTimeInUserZone(dueAt)
                    .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                  patchSelectedTasks({ dueAt: timestamp.toJSDate() });
                }}
                onClose={() => setIsDueAtDatepickerOpen(false)}
                onRemoveValue={e => {
                  e.stopPropagation();
                  patchSelectedTasks({ dueAt: null });
                }}
                shouldCloseOnSelect
                showRemoveValue
                showTimeSelect={false}
              />
            </div>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: 8,
                marginTop: 8,
              }}
            >
              <div data-testid="batch-assignee-selector">
                <AssigneeButton
                  assigneeId={sharedAssigneeId(selectedTasks)}
                  onChange={assignSelectedTasksToUser}
                />
              </div>
              <div data-testid="batch-project-selector">
                <ProjectSelectorButton
                  onSelect={project => assignSelectedTasksToProject(project.id)}
                  projects={availableProjects}
                />
              </div>
            </div>
            {
              isOverBatchLimit && (
                <div role="alert" style={{ marginTop: 8 }}>
                  {t('selectAtMostXItems', { count: MAX_BATCH_SIZE })}
                </div>
              )
            }
          </SelectionBar>
        )
      }
    </>
  );
}

export default TasksView;
